import { createContext, type ComponentType } from 'react';

import type { ListVerdict } from '@/lib/listState';
import type { LegaPosizione, LegaRiga, LegaStagione } from '@/lib/limulo/leghe';

/**
 * LA VISTA DI UNA LEGA che `ClassificaLega` (in `LeghePanel`) passa a una pelle alternativa, quando qualcuno sopra di lei ne ha messa una nel Context. Porta SOLO valori che il pannello ha gia' calcolato: nessun verdetto nuovo, nessuna lettura nuova.
 * «E' mia» resta `eMia` di oggi, passata come booleani (`mie`, una per riga, nello stesso ordine di `righe`): la pelle non la riscrive.
 *
 * @param lega la lega che si guarda.
 * @param conModalita ci sono piu' leghe in corso per il gioco: la modalita' si dice.
 * @param verdict il verdetto di oggi sulla classifica (`listVerdict`): `rows` · `loading` · `slow` · `empty` · `error`…
 * @param righe le righe della classifica, come arrivano (le prime 50).
 * @param mie per ogni riga, se e' la mia (`eMia`).
 * @param io la mia riga dalla posizione, o `null` (nessuna posizione, o non ancora risposta).
 * @param ioInLista la mia riga e' fra le righe mostrate.
 * @param mia la risposta su di me: `undefined` = non ha ancora risposto · `null` = forma ignota · `{ riga }` (anche `null`) = una risposta.
 * @param rifiuto l'errore della lettura (`q.error`, non `isError`), o `undefined`: con un rifiuto del BFF lo stato di oggi e' `LimuloDegraded`.
 * @param totale il `total` della pagina, o `null` quando non c'e': non e' detto che sia certo (`paginaOf` lo riempie con le righe).
 */
export interface VistaLega {
  lega: LegaStagione;
  conModalita: boolean;
  verdict: ListVerdict;
  righe: LegaRiga[];
  mie: boolean[];
  io: LegaRiga | null;
  ioInLista: boolean;
  mia: LegaPosizione | null | undefined;
  rifiuto: unknown;
  totale: number | null;
}

/**
 * LA VISTA DELLA CLASSIFICA che `LeghePanel` passa a una pelle alternativa DOPO il suo verdetto `rows` (le leghe in corso ci sono): le leghe e il componente di oggi per una lega.
 * `Lega` e' una funzione di modulo di `LeghePanel`: l'identita' e' stabile, e la pelle la rende (`<Lega lega conModalita />`) per avere la lettura e il verdetto di oggi.
 */
export interface VistaClassifica {
  leghe: LegaStagione[];
  Lega: ComponentType<{ lega: LegaStagione; conModalita: boolean }>;
}

/**
 * LE PELLI ALTERNATIVE dei due pezzi di `LeghePanel` che hanno logica dentro di se'. Ognuna e' un componente DI MODULO: un valore creato nel render cambierebbe identita' a ogni polling e smonterebbe la vista.
 */
export interface PelleLeghe {
  Classifica?: ComponentType<VistaClassifica>;
  Lega?: ComponentType<{ vista: VistaLega }>;
}

/**
 * IL PUNTO D'INNESTO di una pelle alternativa della classifica delle leghe (stesso schema di `SlotPelleCode`). Il valore di partenza e' `null`: senza nessuno sopra, `LeghePanel` rende quello di sempre e il DOM non cambia di un nodo.
 * ⛔ Il valore di partenza resta `null`: una pelle di default farebbe cambiare il pannello per chiunque, anche dove nessuno l'ha scelta.
 */
export const SlotPelleLeghe = createContext<PelleLeghe | null>(null);
